/* eslint-disable max-lines */
import { Tooltip } from '@heroui/react';
import {
  Edit2, Plus, Smartphone, Trash2,
} from 'lucide-react';
import React, {
  FC, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ModalConfirmation } from '@/components';
import { useUserState } from '@/state';
import {
  Permission,
  Role,
  roleLabels,
  StaffServicePermission,
  staffServicePermissionLabels,
} from '@/views/Settings/queries';

/* ============================================================
   Kompaktowa tabela członków zespołu — w stylu reszty aplikacji
   (taki sam pattern jak SpecialOffer / Channels / Hotel widoki).
   ============================================================ */

const ListTableWrap = styled.div`
  width: 100%;
  overflow-x: auto;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#ffffff')};
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#e5e7eb')};
  border-radius: 12px;
`;

const ListTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 11px;
    letter-spacing: 0.04em;
    font-weight: 400;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.background.tertiary};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
  }
  thead th.center { text-align: left; }
  thead th.right { text-align: left; }

  tbody tr {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
    transition: background 0.15s;
  }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f9fafb')};
  }

  tbody td {
    padding: 8px 16px;
    vertical-align: middle;
    color: ${({ theme }) => theme.colors.text.primary};
  }
  tbody td.center { text-align: center; }
  tbody td.right { text-align: right; }
  tbody td.muted { color: ${({ theme }) => theme.colors.text.secondary}; }
`;

const UserCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const UserName = styled.span`
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.35;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UserEmail = styled.span`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.4;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 300;
  background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f3f4f6')};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StaffPill = styled.span<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px 3px 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 300;
  background: ${({ $active }) => ($active ? 'rgba(16, 185, 129, 0.12)' : 'rgba(148, 163, 184, 0.12)')};
  color: ${({ $active }) => ($active ? '#059669' : '#64748b')};

  svg { width: 12px; height: 12px; }
`;

const CompactIconButton = styled.button<{ $variant?: 'secondary' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#dc2626';
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};

  &:hover:not(:disabled) {
    background: ${({ $variant, theme }) => {
    if ($variant === 'danger') return theme.mode === 'dark' ? 'rgba(239,68,68,0.12)' : '#fee2e2';
    return theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f9fafb';
  }};
    border-color: ${({ $variant, theme }) => {
    if ($variant === 'danger') return '#fecaca';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg { width: 14px; height: 14px; }
`;

const CompactActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  justify-content: flex-end;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

const AddInlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 400;
  min-height: 30px;
  background: #111827;
  color: #ffffff;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.1s ease;

  svg { width: 14px; height: 14px; }

  &:hover:not(:disabled) {
    background: #000000;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.28);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${({ theme }) => (theme.mode === 'dark'
    ? `
    background: #1f2937;
    border-color: #374151;

    &:hover:not(:disabled) { background: #111827; }
  `
    : '')}
`;

const EmptyTableContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  gap: 6px;
  text-align: center;
`;

const EmptyTitle = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EmptyDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 380px;
  line-height: 1.5;
`;

const LoadingSkeleton = styled.div`
  height: 56px;
  width: 100%;
  border-radius: 12px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0.04) 0%,
    rgba(0, 0, 0, 0.07) 50%,
    rgba(0, 0, 0, 0.04) 100%
  );
  background-size: 200% 100%;
  animation: skeletonShimmer 1.6s ease-in-out infinite;

  @keyframes skeletonShimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

interface TeamMember {
  id: string;
  email: string;
  role?: string | string[];
  permissions?: Permission[];
  staffService?: boolean;
  staffServicePermissions?: StaffServicePermission[];
  displayName?: string;
  name?: string;
  surname?: string;
  phoneNumber?: string;
  fcmToken?: string;
  fcmTokens?: string[];
  [key: string]: any;
}

interface TeamMembersSectionProps {
  teamMembers?: TeamMember[];
  isLoading: boolean;
  /**
   * Klik w „edytuj" — formularz jest osobnym WIDOKIEM, więc komponent tylko
   * zgłasza intencję, a nawigację robi strona listy (dawniej: modal tutaj).
   */
  onEditMemberClick?: (memberId: string) => void;
  onDeleteMember?: (memberId: string) => void;
  /**
   * Czy pokazywać akcje na wierszach. Edycja i usuwanie to uprawnienie
   * właściciela/administratora — reszta zespołu widzi listę do odczytu.
   */
  canManage?: boolean;
  isDeleting?: boolean;
}

export const TeamMembersSection: FC<TeamMembersSectionProps> = ({
  teamMembers,
  isLoading,
  onEditMemberClick,
  onDeleteMember,
  canManage = false,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  const { currentUser } = useUserState();
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    memberId: string | null;
    memberEmail: string | null;
  }>({
    isOpen: false,
    memberId: null,
    memberEmail: null,
  });

  const prevIsDeletingRef = useRef(isDeleting);

  useEffect(() => {
    if (prevIsDeletingRef.current && !isDeleting) {
      setDeleteConfirmation({
        isOpen: false,
        memberId: null,
        memberEmail: null,
      });
    }
    prevIsDeletingRef.current = isDeleting;
  }, [isDeleting]);

  const handleDeleteClick = (memberId: string, memberEmail: string) => {
    setDeleteConfirmation({
      isOpen: true,
      memberId,
      memberEmail,
    });
  };

  const handleConfirmDelete = () => {
    if (deleteConfirmation.memberId && onDeleteMember) {
      onDeleteMember(deleteConfirmation.memberId);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      memberId: null,
      memberEmail: null,
    });
  };

  const isEmpty = !isLoading && (!teamMembers || teamMembers.length === 0);

  return (
    <>
      <SectionHeader>
        <span style={{ fontSize: 12, fontWeight: 500, color: '#64748b' }}>
          {teamMembers
            ? t('settings.teamMembersCount', '{{count}} użytkowników', { count: teamMembers.length })
            : ''}
        </span>
      </SectionHeader>

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <LoadingSkeleton />
          <LoadingSkeleton />
          <LoadingSkeleton />
        </div>
      ) : (
        <ListTableWrap>
          <ListTable>
            <thead>
              <tr>
                <th>{t('settings.teamTable.user', 'Użytkownik')}</th>
                <th style={{ width: 140 }}>{t('settings.teamTable.role', 'Rola')}</th>
                <th style={{ width: 160 }}>{t('settings.teamTable.staffApp', 'Aplikacja Staff')}</th>
                <th className="right" style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                <tr>
                  <td colSpan={4} style={{ padding: 0 }}>
                    <EmptyTableContent>
                      <EmptyTitle>
                        {t('settings.noTeamMembers', 'Brak użytkowników')}
                      </EmptyTitle>
                      <EmptyDescription>
                        {t(
                          'settings.noTeamMembersDescription',
                          'Dodaj pierwszego członka zespołu — będzie mógł zarządzać organizacją i korzystać z aplikacji Staff.',
                        )}
                      </EmptyDescription>
                    </EmptyTableContent>
                  </td>
                </tr>
              ) : (
                (teamMembers || []).map((member) => {
                  const userRole = Array.isArray(member.role) ? member.role[0] : member.role;
                  const currentRole = (userRole === 'owner' || userRole === 'user' || userRole === 'manager') ? userRole : 'user';
                  const userPermissions = member.permissions || [];
                  const userStaffService = member.staffService || false;
                  const userStaffServicePermissions = member.staffServicePermissions || [];
                  const userName = member.name || '';
                  const userSurname = member.surname || '';
                  const userPhoneNumber = member.phoneNumber || '';

                  const displayName = member.displayName
                    || (userName || userSurname ? `${userName} ${userSurname}`.trim() : member.email);

                  return (
                    <tr key={member.id}>
                      <td>
                        <UserCell>
                          <UserName>{displayName}</UserName>
                          {displayName !== member.email && (
                            <UserEmail>{member.email}</UserEmail>
                          )}
                        </UserCell>
                      </td>
                      <td>
                        <RoleBadge>
                          {member.role
                            ? Array.isArray(member.role)
                              ? member.role.map((r) => roleLabels[r as Role] || r).join(', ')
                              : roleLabels[member.role as Role] || member.role
                            : 'User'}
                        </RoleBadge>
                      </td>
                      <td>
                        <Tooltip
                          content={
                            userStaffService && userStaffServicePermissions.length > 0
                              ? userStaffServicePermissions
                                .map((perm) => staffServicePermissionLabels[perm] || perm)
                                .join(', ')
                              : t('settings.staffServiceUnavailable', 'Aplikacja Staff niedostępna')
                          }
                          placement="top"
                          radius="sm"
                          size="sm"
                          delay={300}
                          classNames={{
                            base: 'pointer-events-auto',
                            content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                          }}
                        >
                          <StaffPill $active={userStaffService}>
                            <Smartphone />
                            {userStaffService
                              ? t('settings.staffServiceActive', 'Aktywne')
                              : t('settings.staffServiceInactive', 'Nieaktywne')}
                          </StaffPill>
                        </Tooltip>
                      </td>
                      <td className="right">
                        {canManage && (
                        <CompactActions>
                          <Tooltip
                            content={t('common.edit', 'Edytuj')}
                            placement="top"
                            radius="sm"
                            size="sm"
                            delay={300}
                            classNames={{
                              base: 'pointer-events-auto',
                              content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                            }}
                          >
                            <span>
                              <CompactIconButton
                                type="button"
                                $variant="secondary"
                                onClick={() => onEditMemberClick?.(member.id)}
                                disabled={!onEditMemberClick}
                                aria-label={String(t('common.edit', 'Edytuj'))}
                              >
                                <Edit2 />
                              </CompactIconButton>
                            </span>
                          </Tooltip>
                          {member.id !== currentUser?.uid && (
                            <Tooltip
                              content={t('common.delete', 'Usuń')}
                              placement="top"
                              radius="sm"
                              size="sm"
                              delay={300}
                              classNames={{
                                base: 'pointer-events-auto',
                                content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                              }}
                            >
                              <span>
                                <CompactIconButton
                                  type="button"
                                  $variant="danger"
                                  onClick={() => handleDeleteClick(member.id, member.email)}
                                  disabled={isDeleting}
                                  aria-label={String(t('common.delete', 'Usuń'))}
                                >
                                  <Trash2 />
                                </CompactIconButton>
                              </span>
                            </Tooltip>
                          )}
                        </CompactActions>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </ListTable>
        </ListTableWrap>
      )}

      <ModalConfirmation
        isOpen={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        onConfirm={() => handleConfirmDelete()}
        itemId={deleteConfirmation.memberId}
        title={t('settings.deleteTeamMember', 'Usuń użytkownika')}
        description={t(
          'settings.deleteTeamMemberConfirmation',
          `Czy na pewno chcesz usunąć użytkownika ${deleteConfirmation.memberEmail}?`,
          { email: deleteConfirmation.memberEmail },
        )}
        buttonConfirm={t('common.delete', 'Usuń')}
        isLoading={isDeleting}
      />
    </>
  );
};
